import { useCallback, useEffect, useRef, useState } from 'react';
import { SignupError, UNKNOWN_MESSAGE, getSignupStatus } from '../../services/signup';
import type { SignupStatusResponse } from '../../types/signup';

export type SignupStatusState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; status: SignupStatusResponse };

/**
 * Loads GET /api/signup/status once, with a reload.
 *
 * The request counter drops a stale answer: StrictMode double-invokes the effect in development, so
 * two loads really are in flight, and a retry can overtake a slow first attempt.
 */
export const useSignupStatus = () => {
  const [state, setState] = useState<SignupStatusState>({ phase: 'loading' });
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setState({ phase: 'loading' });

    try {
      const status = await getSignupStatus();
      if (requestIdRef.current === requestId) {
        setState({ phase: 'ready', status });
      }
    } catch (error: unknown) {
      if (requestIdRef.current === requestId) {
        setState({
          phase: 'error',
          message: error instanceof SignupError ? error.message : UNKNOWN_MESSAGE,
        });
      }
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { state, reload };
};
